/*
  groom.bg cosmetics calculator core — ONE implementation of the dilution rules for every theme tool
  (bath-cost calculator, mixing helper, finder). The rules are owned by the shared fixture
  Shopify-Catalog-Ops/sites/groom-bg/data/cosmetics_facts_2026-10-03/calc_fixture.json (reference:
  calc_fixture_build.py); the shop assistant tests against the same file. Change the fixture first.

  groom.dilution: "N:1" = N parts water + 1 part product; «до N:1» / "up to N:1" = the maker's upper limit;
  "A:1–B:1" = a range. groom.ready_to_use: true / false / null (unknown).
  Modes → figures (as_sold first, then dilute by increasing N):
    dilute_n        "N:1", RTU false/null                      → [dilute N]
    dilute_upto     «до N:1», RTU false                       → [dilute N, worded as a limit]
    both            «до N:1», RTU true/null; "N:1", RTU true  → [as_sold, dilute N]   (no default; from «до» the
                                                                dilute figure is worded as a limit)
    range_both_ends "A:1–B:1", RTU false/null                 → [dilute A, dilute B]
    both_range      "A:1–B:1", RTU true                       → [as_sold, dilute A, dilute B]
    as_sold         no dilution, RTU true                     → [as_sold]
    per_label       anything else                             → []  («по указанията на етикета», no number)
*/
(function (root) {
  function parse(raw) {
    if (raw == null) return { kind: 'none' };
    let s = String(raw).trim().toLowerCase().replace(/[–—]/g, '-').replace(/,/g, '.');
    const upto = /^(до|up to)/.test(s);
    s = s.replace(/^(до|up to)\s*/, '');
    const num = '(\\d+(?:\\.\\d+)?)\\s*:\\s*1';
    let m = s.match(new RegExp('^' + num + '\\s*-\\s*' + num + '$'));
    if (m) {
      const a = parseFloat(m[1]), b = parseFloat(m[2]);
      return { kind: 'range', lo: Math.min(a, b), hi: Math.max(a, b), n: Math.max(a, b) };
    }
    m = s.match(new RegExp('^' + num + '$'));
    if (m && parseFloat(m[1]) > 0) return { kind: upto ? 'upto' : 'n', n: parseFloat(m[1]) };
    return { kind: 'none' };
  }

  // rtu: true | false | null
  function decide(raw, rtu) {
    const p = parse(raw);
    const neat = { kind: 'as_sold' };
    const dil = (n, limit) => ({ kind: 'dilute', n, limit: !!limit });
    if (p.kind === 'range') {
      const ends = [dil(p.lo), dil(p.hi)];
      return rtu === true ? { mode: 'both_range', figures: [neat].concat(ends) } : { mode: 'range_both_ends', figures: ends };
    }
    if (p.kind === 'upto') {
      if (rtu === false) return { mode: 'dilute_upto', figures: [dil(p.n, true)] };
      return { mode: 'both', figures: [neat, dil(p.n, true)] }; // maker's «до N:1» stays worded as a limit
    }
    if (p.kind === 'n') {
      if (rtu === true) return { mode: 'both', figures: [neat, dil(p.n)] };
      return { mode: 'dilute_n', figures: [dil(p.n)] };
    }
    if (rtu === true) return { mode: 'as_sold', figures: [neat] };
    return { mode: 'per_label', figures: [] };
  }

  /*
    amounts: { solution_ml, neat_ml } for one bath, or { bottle_ml_target } for a mixing bottle.
    pack: { bottle_ml, price } (price in € for the bottle).
    → figures with product_ml, water_ml, cost (unrounded; round for display only).
  */
  function compute(raw, rtu, amounts, pack) {
    const d = decide(raw, rtu);
    const unit = pack && pack.bottle_ml > 0 ? pack.price / pack.bottle_ml : null;
    const target = amounts.bottle_ml_target || null;
    const figures = d.figures.map((f) => {
      if (f.kind === 'as_sold') {
        if (target || amounts.neat_ml == null) return Object.assign({}, f, { product_ml: null, water_ml: null, cost: null });
        return Object.assign({}, f, { product_ml: amounts.neat_ml, water_ml: 0, cost: unit == null ? null : amounts.neat_ml * unit });
      }
      const sol = target || amounts.solution_ml;
      if (sol == null) return Object.assign({}, f, { product_ml: null, water_ml: null, cost: null });
      const prod = sol / (f.n + 1);
      return Object.assign({}, f, { product_ml: prod, water_ml: sol - prod, cost: unit == null ? null : prod * unit });
    });
    return { mode: d.mode, figures };
  }

  /*
    Amount per bath for one figure kind ('dilute' → ml of ready solution, 'as_sold' → ml of product).
    defaults = {ready: {small, medium, large, cat}, neat: {…}, long_factor}. → {ml, source: 'default'}
    Fixture v5 (OWNER 2026-10-04): ALWAYS the owner-confirmed «ориентировъчно» table × long_factor for long coats,
    rounded half up to 50 ml / 5 ml, for every product. groom.usage (maker amounts) stays as data but is NOT used for
    any number until the CC chart is re-verified and the owner re-approves — the usage argument is accepted and ignored.
  */
  function amount(usage, size, longCoat, kind, defaults) {
    const base = defaults[kind === 'dilute' ? 'ready' : 'neat'][size];
    const step = kind === 'dilute' ? 50 : 5;
    return { ml: Math.round((base * (longCoat ? defaults.long_factor : 1)) / step) * step, source: 'default' };
  }

  // The cheapest way the maker allows (used for sorting and the best-case comparison line).
  function best(figures) {
    return figures.filter((f) => f.cost != null).reduce((a, f) => (a == null || f.cost < a.cost ? f : a), null);
  }

  const api = { parse, decide, compute, best, amount };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.GroomCalc = api;
})(typeof window !== 'undefined' ? window : this);
